/**
 * marketing.service.ts
 *
 * API wrappers for the unified Marketing module:
 * SMS campaigns, email templates, SMS templates, automations,
 * audience lists, segments, and analytics.
 */
import { apiClient } from './apiClient';
import { uploadFile } from './uploads.service';
import type {
  RequestInfoBranding,
  RequestInfoConfirmLogoVariant,
  RequestInfoDetailsDisplay,
  RequestInfoLogoVariant,
  RequestInfoResource,
} from '@/pages/request-info/requestInfo.service';

// ─── Shared Types ────────────────────────────────────────────────────────────

/**
 * A request-info lead a campaign or automation message produced, as the
 * contacts lists and contact modals carry it (RequestInfoLeadCounter::
 * forSendContacts). `leadId` is the lead's contacts row: /leads/:leadId.
 */
export interface ContactLead {
  leadId: number;
  /** 'recipient' = this contact tapped; 'referral' = someone else used their link. */
  leadKind: string;
  /** The automation step the message came from; 0 for a campaign. */
  stepId: number;
  stepOrder: number;
  /** Pacific wall clock, like every marketing DATETIME. */
  createdAt: string;
  resourceName: string;
  /** The name on the lead; '' when the lead was deleted. */
  leadName: string;
  /** The lead was hard-deleted in the legacy CRM: nothing to link to. */
  leadDeleted: boolean;
}

/**
 * The contacts endpoints' `lead` filter: any lead (`true`), or one kind, as the
 * stats pages' Leads (recipient) and Referral leads (referral) boxes count.
 */
export type ContactLeadParam = true | 'recipient' | 'referral';

interface PaginatedResponse<T> {
  data: T[];
  meta: { total: number; page: number; perPage: number; totalPages: number };
}

interface ListParams {
  search?: string;
  status?: string;
  folder?: string;
  channel?: string;
  page?: number;
  perPage?: number;
  /**
   * Template and contact lists: 1 = active, 0 = inactive. OMIT for both —
   * the API branches on has('active'), so an empty value reads as 0.
   */
  active?: 0 | 1;
  /** Contact lists only. OMIT for both types. */
  listType?: 'static' | 'dynamic';
  /** Template lists only. Anything unrecognised sorts newest-first. */
  sort?: TemplateSort;
  /**
   * Email template lists only. 1 to include compiled html inline, subject to the
   * API's total-size budget. Leave unset for lists — fetch previews per card
   * instead, or a large library makes the response undeliverable.
   */
  withContent?: 0 | 1;
}

/** Sort orders the email/SMS template lists accept. */
export type TemplateSort = 'newest' | 'oldest' | 'name';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function unwrap(r: any) {
  return r.data.data;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function unwrapList(r: any): any {
  return { data: r.data.data, meta: r.data.meta };
}

// ─── Photo Library ───────────────────────────────────────────────────────────

export interface MarketingPhoto {
  id: number;
  url: string;
  originalFilename: string;
  folder: string;
  folderID?: number;
  fileSize: number;
  width?: number;
  height?: number;
  /** True when the image is shared with every account under the master account. */
  shared?: boolean;
  /** False when the caller opted out of the library and this is a one-off campaign asset. */
  savedToLibrary?: boolean;
}

/**
 * Upload a photo for a campaign, template or automation.
 *
 * `saveToLibrary` defaults to FALSE. Uploads used to land in the photo library
 * unconditionally, so a one-off hero image for a single email was stuck there
 * forever. With it off the API writes the object to S3 under
 * marketing/{account}/campaign-assets/ and returns the URL without creating a
 * library row — the image works on that campaign and nowhere else.
 *
 * Files over 4 MB are routed through the presigned-S3 flow (uploadFile) to
 * bypass Lambda's 6 MB payload limit; smaller files still go multipart under the
 * `image` field.
 */
export function uploadMarketingPhoto(
  file: File,
  opts: {
    altText?: string;
    folder?: string;
    folderId?: number | null;
    saveToLibrary?: boolean;
    onProgress?: (pct: number) => void;
  } = {},
): Promise<MarketingPhoto> {
  const saveToLibrary = opts.saveToLibrary ?? false;
  const fields: Record<string, string> = {
    alt_text: opts.altText ?? '',
    save_to_library: saveToLibrary ? '1' : '0',
  };
  if (saveToLibrary) {
    if (opts.folderId) fields.folder_id = String(opts.folderId);
    fields.folder = opts.folder ?? 'general';
  }

  return uploadFile('/marketing/photos', file, fields, {
    fileFieldName: 'image',
    onProgress: opts.onProgress,
  }).then((r) => r.data.data as MarketingPhoto);
}

/**
 * A folder row as the photo endpoints return it.
 *
 * `scope` is 'master' (created in the corporate dashboard, shared with every
 * account under the master) or 'account' (created here, private to this
 * account). `isSystem` marks the nine locked folders that back products,
 * appointments, memberships, packages, facilities, logos, alerts, marketing and
 * general — those can never be renamed or deleted, because entity pickers
 * address them by name.
 */
interface PhotoFolderRow {
  id: number | null;
  /** The photo endpoints spell the name `folder`; callers get `name`. */
  folder: string;
  count: number;
  scope?: 'master' | 'account';
  isSystem?: boolean;
  systemKey?: string;
}

const toLibraryFolder = (f: PhotoFolderRow): LibraryFolder => ({
  id: f.id,
  name: f.folder,
  count: f.count ?? 0,
  scope: f.scope ?? 'master',
  isSystem: f.isSystem ?? false,
  systemKey: f.systemKey ?? '',
});

export function getPhotoFolders(): Promise<LibraryFolder[]> {
  return apiClient
    .get('/marketing/photos/folders')
    .then((r) => ((r.data?.data ?? []) as PhotoFolderRow[]).map(toLibraryFolder));
}

/**
 * Folder management from Prospera creates ACCOUNT-scoped folders, private to
 * this account. The API rejects any attempt to rename or delete a system folder
 * or a master folder, so the manager only offers those actions on rows this
 * account owns.
 */
export function createPhotoFolder(name: string): Promise<LibraryFolder> {
  return apiClient.post('/marketing/photos/folders', { name }).then((r) => toLibraryFolder(r.data?.data));
}

export function renamePhotoFolder(id: number, name: string): Promise<LibraryFolder> {
  return apiClient
    .put(`/marketing/photos/folders/${id}`, { name })
    .then((r) => toLibraryFolder(r.data?.data));
}

export function deletePhotoFolder(id: number): Promise<void> {
  return apiClient.delete(`/marketing/photos/folders/${id}`).then(() => undefined);
}

/**
 * Soft delete — the API flips accounts_public_photos.active to 0; the row and
 * the S3 object both survive. Only works on photos this account owns: the API
 * returns 422 for a master-shared image (managed in the corporate dashboard) and
 * for one still attached to a product, facility, membership or appointment type.
 */
export function deleteMarketingPhoto(id: number): Promise<void> {
  return apiClient.delete(`/marketing/photos/${id}`).then(() => undefined);
}

// ─── Video Library ───────────────────────────────────────────────────────────

export interface MarketingVideo {
  id: number;
  url: string;
  originalFilename: string;
  folder: string;
  folderID?: number;
  fileSize: number;
  mimeType?: string;
  duration?: number;
  width?: number;
  height?: number;
  shared?: boolean;
  savedToLibrary?: boolean;
}

/** Matches VideoLibraryService::MAX_BYTES on the API. */
export const MAX_VIDEO_BYTES = 500 * 1024 * 1024;

export const MAX_VIDEO_LABEL = '500MB';

export const ALLOWED_VIDEO_MIME_TYPES = [
  'video/mp4',
  'video/quicktime',
  'video/webm',
  'video/ogg',
  'video/x-m4v',
  'video/mpeg',
];

export const VIDEO_ACCEPT_ATTR = ALLOWED_VIDEO_MIME_TYPES.join(',');

export const VIDEO_FORMAT_LABEL = 'MP4, MOV, WebM, OGG';

/**
 * Whether the browser reported a type we accept.
 *
 * An empty type is allowed through and checked by extension instead: browsers
 * routinely report '' for .mov and some .m4v files, and rejecting on that alone
 * would block valid uploads. The API re-checks whatever type it is given.
 */
export function isAllowedVideoType(file: File): boolean {
  if (file.type && ALLOWED_VIDEO_MIME_TYPES.includes(file.type)) return true;
  if (file.type && file.type.startsWith('video/')) return true;
  if (!file.type) return /\.(mp4|mov|webm|ogv|ogg|m4v|mpe?g)$/i.test(file.name);
  return false;
}

/**
 * The type to presign a video as. Browsers often report '' for .mov and .m4v,
 * and the presign only allows the library's 500 MB ceiling for a video/* type.
 */
function videoContentType(file: File): string {
  if (file.type.startsWith('video/')) return file.type;
  const byExtension: Record<string, string> = {
    mp4: 'video/mp4', mov: 'video/quicktime', webm: 'video/webm', ogv: 'video/ogg',
    ogg: 'video/ogg', m4v: 'video/x-m4v', mpg: 'video/mpeg', mpeg: 'video/mpeg',
  };
  return byExtension[file.name.split('.').pop()?.toLowerCase() ?? ''] ?? 'video/mp4';
}

/** '1:04', or '' when the duration is unknown. */
export function formatDuration(seconds?: number): string {
  if (!seconds || seconds < 0 || !Number.isFinite(seconds)) return '';
  const total = Math.round(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * Reads duration and pixel size out of a local file before it is uploaded.
 *
 * The API has no ffprobe, so the browser is the only place this metadata can
 * come from. Resolves with zeros rather than rejecting: missing metadata costs a
 * line in the details panel and is not worth failing an upload over.
 */
export function probeVideoFile(
  file: File,
): Promise<{ duration: number; width: number; height: number }> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const el = document.createElement('video');
    const done = (r: { duration: number; width: number; height: number }) => {
      URL.revokeObjectURL(url);
      resolve(r);
    };
    el.preload = 'metadata';
    el.onloadedmetadata = () =>
      done({
        duration: Number.isFinite(el.duration) ? Math.round(el.duration) : 0,
        width: el.videoWidth || 0,
        height: el.videoHeight || 0,
      });
    el.onerror = () => done({ duration: 0, width: 0, height: 0 });
    // A file the browser cannot decode would otherwise leave the caller waiting
    // on a metadata event that never fires.
    setTimeout(() => done({ duration: 0, width: 0, height: 0 }), 10000);
    el.src = url;
  });
}

/**
 * Upload a video for a campaign, template or automation.
 *
 * `saveToLibrary` defaults to FALSE, matching uploadMarketingPhoto: a one-off
 * video for a single send should not be stuck in the library forever.
 *
 * Every library video is over the 4 MB multipart threshold in practice, so
 * uploadFile() routes it through the presigned-S3 flow automatically; the API
 * resolves the resulting s3Key back into a normal file.
 */
export function uploadMarketingVideo(
  file: File,
  opts: {
    altText?: string;
    folder?: string;
    folderId?: number | null;
    saveToLibrary?: boolean;
    onProgress?: (pct: number, loaded: number, total: number) => void;
  } = {},
): Promise<MarketingVideo> {
  const saveToLibrary = opts.saveToLibrary ?? false;
  const fields: Record<string, string> = {
    alt_text: opts.altText ?? '',
    save_to_library: saveToLibrary ? '1' : '0',
  };
  if (saveToLibrary) {
    if (opts.folderId) fields.folder_id = String(opts.folderId);
    fields.folder = opts.folder ?? 'general';
  }

  // Duration cannot be derived server-side, so send what the browser measured.
  return probeVideoFile(file).then((meta) => {
    fields.duration = String(meta.duration);
    fields.width = String(meta.width);
    fields.height = String(meta.height);

    return uploadFile('/marketing/videos', file, fields, {
      fileFieldName: 'video',
      onProgress: opts.onProgress,
      presign: { purpose: 'video_library', contentType: videoContentType(file) },
    }).then((r) => r.data.data as MarketingVideo);
  });
}

export function getVideoFolders(): Promise<LibraryFolder[]> {
  return apiClient
    .get('/marketing/videos/folders')
    .then((r) => ((r.data?.data ?? []) as PhotoFolderRow[]).map(toLibraryFolder));
}

/**
 * Folder management from Prospera creates ACCOUNT-scoped video folders, private
 * to this account. The API rejects any attempt to rename or delete a system
 * folder or a master folder, exactly as for photos.
 */
export function createVideoFolder(name: string): Promise<LibraryFolder> {
  return apiClient.post('/marketing/videos/folders', { name }).then((r) => toLibraryFolder(r.data?.data));
}

export function renameVideoFolder(id: number, name: string): Promise<LibraryFolder> {
  return apiClient
    .put(`/marketing/videos/folders/${id}`, { name })
    .then((r) => toLibraryFolder(r.data?.data));
}

export function deleteVideoFolder(id: number): Promise<void> {
  return apiClient.delete(`/marketing/videos/folders/${id}`).then(() => undefined);
}

/**
 * Soft delete — the API flips accounts_public_videos.active to 0; the row and
 * the S3 object both survive. Only works on videos this account owns: the API
 * returns 422 for a master-shared video, which is managed in the corporate
 * dashboard.
 */
export function deleteMarketingVideo(id: number): Promise<void> {
  return apiClient.delete(`/marketing/videos/${id}`).then(() => undefined);
}

/** Bulk-file this account's own videos into a folder. */
export function moveMarketingVideos(videoIds: number[], folder: string): Promise<{ moved: number }> {
  return apiClient
    .post('/marketing/videos/move', { videoIds, folder })
    .then((r) => r.data?.data ?? { moved: 0 });
}

/**
 * What the campaign Photo Library accepts.
 *
 * Two tiers, mirroring ImageFormatNormalizer on the API, which is the gate that
 * actually enforces this:
 *
 *   JPEG, PNG, GIF   stored exactly as uploaded — all three render in the app,
 *                    in email and in a picture message
 *   WebP, BMP, TIFF  accepted, then re-encoded to JPEG (or PNG when the image
 *                    has transparency) before anything else sees them
 *
 * Everything else — HEIC, AVIF, SVG — is refused, because GD has no decoder for
 * them and so there is nothing to convert them to.
 *
 * The conversion tier exists because this pool feeds three destinations that
 * disagree about what an image is. WebP renders in the app, breaks in classic
 * Outlook for Windows, and is rejected outright by Twilio for MMS — which is how
 * one WebP took down a whole SMS campaign.
 */
export const ALLOWED_PHOTO_MIME_TYPES = [
  'image/jpeg', 'image/png', 'image/gif',
  'image/webp', 'image/bmp', 'image/x-ms-bmp', 'image/tiff',
];

/** Formats with no decoder on the API, refused before the upload starts. */
export const REJECTED_PHOTO_MIME_TYPES = [
  'image/heic', 'image/heif', 'image/avif', 'image/svg+xml',
];

export const PHOTO_ACCEPT_ATTR =
  'image/jpeg,image/png,image/gif,image/webp,image/bmp,image/tiff,'
  + '.jpg,.jpeg,.png,.gif,.webp,.bmp,.tif,.tiff';

export const PHOTO_FORMAT_LABEL = 'JPEG, PNG, GIF, WebP, BMP or TIFF';

/**
 * Whether this file may go in the campaign Photo Library.
 *
 * Extension as well as reported type, for the same reason the social helper does
 * it: a .heic routinely arrives with an empty type from a desktop file picker,
 * and accepting it on that basis is how it reaches a campaign that cannot send.
 */
export function isAllowedPhotoType(file: File): boolean {
  if (/\.(heic|heif|avif|svg)$/i.test(file.name)) return false;
  if (file.type) return ALLOWED_PHOTO_MIME_TYPES.includes(file.type);
  return /\.(jpe?g|png|gif|webp|bmp|tiff?)$/i.test(file.name);
}

/** Why a file was refused, in words the uploader can act on. */
export function photoTypeRejectionReason(file: File): string {
  if (/\.(heic|heif)$/i.test(file.name) || /heic|heif/i.test(file.type)) {
    return 'HEIC images cannot be used here. That is the format iPhones shoot by '
      + 'default — in Photos, choose Share and pick "Most Compatible", or export '
      + 'the picture as a JPEG.';
  }
  if (/\.svg$/i.test(file.name) || file.type === 'image/svg+xml') {
    return 'SVG images cannot be used here, because email and picture messaging '
      + 'cannot display them. Export the artwork as a PNG.';
  }
  return `That image format is not supported. Use ${PHOTO_FORMAT_LABEL}.`;
}

// ─── Social Post Templates ───────────────────────────────────────────────────
//
// The master account's shared default posts plus this account's own. Templates,
// not published posts: Zernio owns real publishing and keeps no local row, so a
// template is picked up by the composer rather than posted from here.
//
// Ownership mirrors the photo and video libraries — a `shared` template belongs
// to the master account and is read-only here.

export interface SocialPostMedia {
  type: 'image' | 'video';
  sourceType: 'photo-library' | 'video-library' | 'social-photo-library' | 'social-video-library' | 'upload';
  sourceID: number;
  url: string;
  originalFilename: string;
  altText: string;
  fileSize: number;
  mimeType: string;
  duration: number;
  width: number;
  height: number;
  /** A signed display link, on PR previews whose bucket is private. Shown, never saved or sent. */
  previewUrl?: string;
}

export interface SocialPostTemplate {
  id: number;
  accountID: number;
  masterAccountID: number;
  /** True when the template is the master account's, shared with every sibling. */
  shared: boolean;
  deleted: boolean;
  /** The template this one is a copy of (cloned by corporate); null for an original. */
  sourcePostID: number | null;
  caption: string;
  folder: string;
  folderID: number;
  mediaItems: SocialPostMedia[];
  coverImageUrl: string | null;
  /** A signed display link for the cover, on PR previews whose bucket is private. Shown, never saved or sent. */
  coverPreviewUrl?: string | null;
  coverOffsetMs: number | null;
  createDate: string;
}

export interface SocialPostTemplateInput {
  caption: string;
  folderId?: number;
  folder?: string;
  mediaItems?: SocialPostMedia[];
  coverImageUrl?: string | null;
  coverOffsetMs?: number | null;
}

/** 20 MB / 500 MB — our ceilings, enforced by the API as well. Each network's own limit applies beneath them. */
export const MAX_POST_IMAGE_BYTES = 20 * 1024 * 1024;
export const MAX_POST_VIDEO_BYTES = 500 * 1024 * 1024;
export const MAX_POST_VIDEO_LABEL = '500MB';

export function getSocialPostTemplates(params?: {
  folder?: string;
  search?: string;
  scope?: 'all' | 'mine' | 'shared';
}): Promise<SocialPostTemplate[]> {
  return apiClient
    .get('/marketing/social-posts', { params })
    .then((r) => (r.data?.data ?? []) as SocialPostTemplate[]);
}

/**
 * Creates an ACCOUNT-scoped template, private to this account. Master-wide
 * defaults are authored in the corporate dashboard.
 */
export function createSocialPostTemplate(input: SocialPostTemplateInput): Promise<SocialPostTemplate> {
  return apiClient.post('/marketing/social-posts', input).then((r) => r.data?.data as SocialPostTemplate);
}

/** Partial edit. Only works on templates this account owns. */
export function updateSocialPostTemplate(
  id: number,
  input: Partial<SocialPostTemplateInput>,
): Promise<SocialPostTemplate> {
  return apiClient.put(`/marketing/social-posts/${id}`, input).then((r) => r.data?.data as SocialPostTemplate);
}

/**
 * Soft delete — the API flips active to 0; the row survives. Returns 422 for a
 * master-shared template, which is removed in the corporate dashboard.
 */
export function deleteSocialPostTemplate(id: number): Promise<void> {
  return apiClient.delete(`/marketing/social-posts/${id}`).then(() => undefined);
}

/** Bulk-file this account's own templates into a folder. */
/** By folder id: a corporate folder and one of this account's can share a name. */
export function moveSocialPostTemplates(postIds: number[], folderId: number): Promise<{ moved: number }> {
  return apiClient
    .post('/marketing/social-posts/move', { postIds, folderId })
    .then((r) => r.data?.data ?? { moved: 0 });
}

export function getSocialPostFolders(): Promise<LibraryFolder[]> {
  return apiClient
    .get('/marketing/social-posts/folders')
    .then((r) => ((r.data?.data ?? []) as PhotoFolderRow[]).map(toLibraryFolder));
}

export function createSocialPostFolder(name: string): Promise<LibraryFolder> {
  return apiClient
    .post('/marketing/social-posts/folders', { name })
    .then((r) => toLibraryFolder(r.data?.data));
}

export function renameSocialPostFolder(id: number, name: string): Promise<LibraryFolder> {
  return apiClient
    .put(`/marketing/social-posts/folders/${id}`, { name })
    .then((r) => toLibraryFolder(r.data?.data));
}


export function deleteSocialPostFolder(id: number): Promise<{ templatesDeleted: number }> {
  return apiClient
    .delete(`/marketing/social-posts/folders/${id}`)
    .then((r) => ({ templatesDeleted: Number(r.data?.data?.templatesDeleted ?? 0) }));
}

// ─── Saved for later (social posts) ──────────────────────────────────────────
//
// A post written in the New social post composer but not yet published or
// scheduled. Kept in Prospera (Zernio holds no draft of it), shown first in My
// Posts with a "To be scheduled later" badge, and deleted once it goes out.

export interface SocialPostDraft {
  id: number;
  caption: string;
  mediaItems: SocialPostMedia[];
  coverImageUrl: string | null;
  coverOffsetMs: number | null;
  /** Zernio account ids that were ticked. */
  zernioAccountIds: string[];
  postType: 'feed' | 'story' | 'reel';
  /** "YYYY-MM-DDTHH:MM" as typed, in `timezone`. Never schedules on its own. */
  scheduledFor: string | null;
  timezone: string | null;
  createDate: string;
  updateDate: string;
}

export type SocialPostDraftInput = Omit<SocialPostDraft, 'id' | 'createDate' | 'updateDate'>;

export function getSocialPostDrafts(): Promise<SocialPostDraft[]> {
  return apiClient.get('/marketing/social-post-drafts').then((r) => (r.data?.data ?? []) as SocialPostDraft[]);
}

export function createSocialPostDraft(input: SocialPostDraftInput): Promise<SocialPostDraft> {
  return apiClient.post('/marketing/social-post-drafts', input).then((r) => r.data?.data as SocialPostDraft);
}

export function updateSocialPostDraft(id: number, input: Partial<SocialPostDraftInput>): Promise<SocialPostDraft> {
  return apiClient.put(`/marketing/social-post-drafts/${id}`, input).then((r) => r.data?.data as SocialPostDraft);
}

export function deleteSocialPostDraft(id: number): Promise<void> {
  return apiClient.delete(`/marketing/social-post-drafts/${id}`).then(() => undefined);
}

// ─── My Posts folders ────────────────────────────────────────────────────────
//
// My Posts' own folders, one private set per account (not the template
// library's). General is folder 0: it always exists, and any post without a
// placement is in it. Posts live in Zernio, so a placement records which folder
// a post is in — by its Zernio id, or by the id of a post saved for later.

export interface MyPostFolder {
  id: number;
  name: string;
}

export type MyPostItemType = 'zernio' | 'draft';

export interface MyPostPlacement {
  type: MyPostItemType;
  key: string;
  folderId: number;
}

export function getMyPostFolders(): Promise<{ folders: MyPostFolder[]; placements: MyPostPlacement[] }> {
  return apiClient.get('/marketing/my-post-folders').then((r) => ({
    folders: (r.data?.data?.folders ?? []) as MyPostFolder[],
    placements: (r.data?.data?.placements ?? []) as MyPostPlacement[],
  }));
}

export function createMyPostFolder(name: string): Promise<MyPostFolder> {
  return apiClient.post('/marketing/my-post-folders', { name }).then((r) => r.data?.data as MyPostFolder);
}

export function renameMyPostFolder(id: number, name: string): Promise<MyPostFolder> {
  return apiClient.put(`/marketing/my-post-folders/${id}`, { name }).then((r) => r.data?.data as MyPostFolder);
}

/** Deletes the folder only; its posts move to General. */
export function deleteMyPostFolder(id: number): Promise<{ movedToGeneral: number }> {
  return apiClient
    .delete(`/marketing/my-post-folders/${id}`)
    .then((r) => ({ movedToGeneral: Number(r.data?.data?.movedToGeneral ?? 0) }));
}

/** File posts into a folder; 0 = General. */
export function placeMyPosts(items: { type: MyPostItemType; key: string }[], folderId: number): Promise<{ placed: number }> {
  return apiClient
    .post('/marketing/my-post-folders/place', { items, folderId })
    .then((r) => ({ placed: Number(r.data?.data?.placed ?? 0) }));
}

// ─── SMS Campaigns ───────────────────────────────────────────────────────────

export interface SmsCampaign {
  smsCampaignId: number;
  name: string;
  description: string | null;
  messageBody: string | null;
  mediaUrls: string[] | null;
  status: string;
  fromNumber: string | null;
  scheduledAt: string | null;
  sentAt: string | null;
  totalRecipients: number;
  accountUsers: boolean;
  createDate: string;
}

export interface CreateSmsCampaignData {
  name: string;
  description?: string;
  messageBody?: string;
}

export function listSmsCampaigns(params: ListParams): Promise<PaginatedResponse<SmsCampaign>> {
  return apiClient.get<{ data: SmsCampaign[]; meta: PaginatedResponse<SmsCampaign>['meta'] }>('/marketing/sms-campaigns', { params }).then(unwrapList);
}

export function createSmsCampaign(data: CreateSmsCampaignData): Promise<SmsCampaign> {
  return apiClient.post<{ data: SmsCampaign }>('/marketing/sms-campaigns', data).then(unwrap);
}

export function getSmsCampaign(id: number): Promise<SmsCampaign> {
  return apiClient.get(`/marketing/sms-campaigns/${id}`).then(unwrap);
}

export function updateSmsCampaign(id: number, data: Partial<CreateSmsCampaignData & { mediaUrls?: string[]; scheduledAt?: string | null; accountUsers?: boolean }>): Promise<SmsCampaign> {
  return apiClient.put(`/marketing/sms-campaigns/${id}`, data).then(unwrap);
}

export function deleteSmsCampaign(id: number): Promise<void> {
  return apiClient.delete(`/marketing/sms-campaigns/${id}`);
}

export function getSmsCampaignContacts(id: number): Promise<SmsCampaignContact[]> {
  return apiClient.get(`/marketing/sms-campaigns/${id}/contacts`).then(r => r.data.data);
}

export function getSmsCampaignLists(id: number): Promise<SmsCampaignList[]> {
  return apiClient.get(`/marketing/sms-campaigns/${id}/lists`).then(r => r.data.data);
}

export function addSmsCampaignList(id: number, listId: number): Promise<{ totalRecipients: number }> {
  return apiClient.post(`/marketing/sms-campaigns/${id}/lists`, { listId }).then(unwrap);
}

export function removeSmsCampaignList(id: number, listId: number): Promise<{ totalRecipients: number }> {
  return apiClient.delete(`/marketing/sms-campaigns/${id}/lists/${listId}`).then(r => r.data.data ?? { totalRecipients: 0 });
}

export function getSmsCampaignRecipientCount(id: number): Promise<{ count: number }> {
  return apiClient.get(`/marketing/sms-campaigns/${id}/recipient-count`).then(unwrap);
}

export interface SmsCampaignContact {
  smsCampaignContactID: number;
  firstName: string | null;
  lastName: string | null;
  pFirstName?: string | null;
  pLastName?: string | null;
  phoneNumber: string | null;
  patientID?: number | null;
  deliveryStatus: string;
  errorMessage?: string | null;
  sentAt: string | null;
  deliveredAt: string | null;
  createDate: string | null;
  /** Request-info leads this contact's text produced. Absent on older responses. */
  leads?: ContactLead[];
}

export interface SmsCampaignContactStatusCounts {
  sent: number;
  delivered: number;
  queued: number;
  failed: number;
  undelivered: number;
  failed_system: number;
  failed_permanent: number;
  unsubscribed: number;
  /** Texts still sending (Queued counts only queued ones). */
  sending?: number;
  /** Contacts whose text produced a request-info lead, either kind, then each kind. */
  lead?: number;
  leadRecipient?: number;
  leadReferral?: number;
}

export function getSmsCampaignContactsPaginated(
  id: number,
  params?: { search?: string; page?: number; perPage?: number; contactFilter?: string; lead?: ContactLeadParam },
): Promise<{ data: SmsCampaignContact[]; meta: { page: number; perPage: number; total: number }; statusCounts: SmsCampaignContactStatusCounts }> {
  return apiClient
    .get(`/marketing/sms-campaigns/${id}/contacts`, { params })
    .then((r) => {
      const payload = r.data;
      return {
        data: payload?.data ?? [],
        meta: payload?.meta ?? { page: 1, perPage: 25, total: 0 },
        statusCounts: payload?.statusCounts ?? { sent: 0, delivered: 0, queued: 0, failed: 0, undelivered: 0, failed_system: 0, failed_permanent: 0, unsubscribed: 0 },
      };
    });
}

export interface SmsContactEvent {
  eventType: string;
  eventDate: string | null;
  errorCode: string | null;
  errorMessage: string | null;
}

export interface SmsContactDetailResponse {
  contact: {
    smsCampaignContactID: number;
    phoneNumber: string;
    firstName: string;
    lastName: string;
    email: string;
    patientID: number | null;
    deliveryStatus: string;
    errorCode: string | null;
    errorMessage: string | null;
    twilioMessageSid: string | null;
    sentAt: string | null;
    deliveredAt: string | null;
    createDate: string | null;
  };
  events: SmsContactEvent[];
  /** Request-info leads this text produced. Absent on older responses. */
  leads?: ContactLead[];
}

export function getSmsCampaignContactDetail(
  campaignId: number,
  contactId: number,
): Promise<SmsContactDetailResponse> {
  return apiClient.get(`/marketing/sms-campaigns/${campaignId}/contacts/${contactId}`).then(unwrap);
}

export interface SmsCampaignList {
  emailListId: number;
  listName: string;
  listType: 'static' | 'dynamic';
  memberCount: number;
  uniqueEmailCount?: number;
  uniquePhoneCount?: number;
}

export function sendSmsCampaign(id: number): Promise<{ sent: number; failed: number }> {
  return apiClient.post(`/marketing/sms-campaigns/${id}/send`).then(unwrap);
}

export function scheduleSmsCampaign(id: number, scheduledAt: string): Promise<void> {
  return apiClient.post(`/marketing/sms-campaigns/${id}/schedule`, { scheduledAt });
}

export function cancelSmsSchedule(id: number): Promise<void> {
  return apiClient.post(`/marketing/sms-campaigns/${id}/cancel-schedule`);
}

export interface SmsCampaignStatsResponse {
  campaign: {
    smsCampaignID: number;
    name: string;
    description: string;
    messageBody: string;
    fromNumber: string;
    status: string;
    sentAt: string | null;
    createDate: string | null;
    totalRecipients: number;
    mediaUrls: string[];
  };
  stats: {
    totalSent: number;
    totalDelivered: number;
    totalFailed: number;
    totalRetryableFailed: number;
    totalUndelivered: number;
    totalUnsubscribed: number;
    totalQueued: number;
    totalSending: number;
    total: number;
  };
  sqs?: {
    ok: boolean;
    pending?: number;
    in_flight?: number;
    worker_active?: boolean;
    error?: string;
  };
  lists: Array<{ id: number; name: string; description?: string }>;
  contactsCount: number;
  /** Request-info leads this campaign produced. Absent from older API builds — read via leadCountsOf(). */
  leads?: RequestInfoLeadCounts;
}

export function getSmsCampaignStats(id: number): Promise<SmsCampaignStatsResponse> {
  return apiClient.get(`/marketing/sms-campaigns/${id}/stats`).then(unwrap);
}

export interface SmsResumeResponse {
  success: boolean;
  message: string;
  queuedCount: number;
  stuckSendingReset: number;
  sqs: { ok: boolean; pending?: number; in_flight?: number; worker_active?: boolean; error?: string };
}

export function resumeSmsCampaign(campaignId: number): Promise<SmsResumeResponse> {
  return apiClient.post(`/marketing/sms-campaigns/${campaignId}/resume`).then(unwrap);
}

export interface SmsRetryFailedResponse {
  success: boolean;
  message: string;
  resetCount: number;
}

export function retryFailedSmsCampaign(campaignId: number, errorPattern?: string): Promise<SmsRetryFailedResponse> {
  return apiClient.post(`/marketing/sms-campaigns/${campaignId}/retry-failed`, { errorPattern }).then(unwrap);
}

export function sendTestSms(id: number, phone: string): Promise<void> {
  return apiClient.post(`/marketing/sms-campaigns/${id}/test`, { phone });
}

// ─── SMS Direct Contacts ────────────────────────────────────────────────────

export interface SmsCampaignDirectContact {
  smsCampaignDirectContactID: number;
  smsCampaignID: number;
  patientID: number | null;
  phoneNumber: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  createDate: string;
  status: number;
}

export interface SmsCampaignExcludeContact {
  smsCampaignExcludeID: number;
  smsCampaignID: number;
  patientID: number | null;
  phoneNumber: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  createDate: string;
  status: number;
}

export function getSmsCampaignDirectContactCount(id: number): Promise<number> {
  return apiClient
    .get(`/marketing/sms-campaigns/${id}/direct-contact-count`)
    .then((r) => r.data?.data?.count ?? 0);
}

export function getSmsCampaignDirectContacts(
  id: number,
  params?: { search?: string; page?: number; perPage?: number; showDeleted?: boolean },
): Promise<{ data: SmsCampaignDirectContact[]; meta: { page: number; perPage: number; total: number } }> {
  return apiClient
    .get(`/marketing/sms-campaigns/${id}/direct-contacts`, { params })
    .then((r) => ({
      data: r.data?.data ?? [],
      meta: r.data?.meta ?? { page: 1, perPage: 25, total: 0 },
    }));
}

export function addSmsCampaignDirectContact(
  id: number,
  data: { patientID?: number; phoneNumber: string; firstName?: string; lastName?: string; email?: string; facilityID?: number; countryCode?: string },
): Promise<{ total_recipients: number }> {
  return apiClient
    .post(`/marketing/sms-campaigns/${id}/direct-contacts`, data)
    .then((r) => r.data?.data ?? r.data);
}

export function removeSmsCampaignDirectContact(id: number, contactId: number): Promise<{ total_recipients: number }> {
  return apiClient
    .delete(`/marketing/sms-campaigns/${id}/direct-contacts/${contactId}`)
    .then((r) => r.data?.data ?? r.data);
}

export function restoreSmsCampaignDirectContact(id: number, contactId: number): Promise<{ total_recipients: number }> {
  return apiClient
    .post(`/marketing/sms-campaigns/${id}/direct-contacts/${contactId}/restore`)
    .then((r) => r.data?.data ?? r.data);
}

export function getSmsCampaignExcludeContactCount(id: number): Promise<number> {
  return apiClient
    .get(`/marketing/sms-campaigns/${id}/exclude-contact-count`)
    .then((r) => r.data?.data?.count ?? 0);
}

export function getSmsCampaignExcludeContacts(
  id: number,
  params?: { search?: string; page?: number; perPage?: number; showDeleted?: boolean },
): Promise<{ data: SmsCampaignExcludeContact[]; meta: { page: number; perPage: number; total: number } }> {
  return apiClient
    .get(`/marketing/sms-campaigns/${id}/exclude-contacts`, { params })
    .then((r) => ({
      data: r.data?.data ?? [],
      meta: r.data?.meta ?? { page: 1, perPage: 25, total: 0 },
    }));
}

export function addSmsCampaignExcludeContact(
  id: number,
  data: { patientID?: number; phoneNumber: string; firstName?: string; lastName?: string; email?: string; facilityID?: number; countryCode?: string },
): Promise<{ total_recipients: number }> {
  return apiClient
    .post(`/marketing/sms-campaigns/${id}/exclude-contacts`, data)
    .then((r) => r.data?.data ?? r.data);
}

export function removeSmsCampaignExcludeContact(id: number, excludeId: number): Promise<{ total_recipients: number }> {
  return apiClient
    .delete(`/marketing/sms-campaigns/${id}/exclude-contacts/${excludeId}`)
    .then((r) => r.data?.data ?? r.data);
}

export function restoreSmsCampaignExcludeContact(id: number, excludeId: number): Promise<{ total_recipients: number }> {
  return apiClient
    .post(`/marketing/sms-campaigns/${id}/exclude-contacts/${excludeId}/restore`)
    .then((r) => r.data?.data ?? r.data);
}

export function toggleSmsAccountUsers(id: number, enabled: boolean): Promise<{ total_recipients: number; accountUsers: number }> {
  return apiClient
    .post(`/marketing/sms-campaigns/${id}/toggle-account-users`, { enabled })
    .then((r) => r.data?.data ?? r.data);
}

// ─── SMS Clone Campaign ─────────────────────────────────────────────────────

export function cloneSmsCampaign(id: number | string, name: string): Promise<SmsCampaign> {
  return apiClient
    .post(`/marketing/sms-campaigns/${id}/clone`, { name })
    .then((r) => r.data?.data ?? r.data);
}

// ─── Email Campaign Actions ──────────────────────────────────────────────────

// (sendEmailCampaign removed: it posted to /marketing/email-campaigns/{id}/send,
// a route that does not exist. The real send is campaigns.service sendCampaign.)

export function scheduleEmailCampaign(id: number, scheduledAt: string): Promise<void> {
  return apiClient.post(`/marketing/email-campaigns/${id}/schedule`, { scheduledAt });
}

export function cancelEmailSchedule(id: number): Promise<void> {
  return apiClient.post(`/marketing/email-campaigns/${id}/cancel-schedule`);
}

export function getEmailCampaignStats(id: number): Promise<CampaignStats> {
  return apiClient.get(`/marketing/email-campaigns/${id}/stats`).then(unwrap);
}

export function sendTestEmailCampaign(id: number, email: string): Promise<void> {
  return apiClient.post(`/marketing/email-campaigns/${id}/test`, { email });
}

// ─── Campaign Stats Type ─────────────────────────────────────────────────────

export interface CampaignStats {
  totalSent: number;
  totalDelivered: number;
  totalFailed: number;
  totalOpened: number;
  totalClicked: number;
  totalBounced: number;
  totalUnsubscribed: number;
  totalUndelivered: number;
  totalOptedOut: number;
}

// ─── Library folders (email + SMS templates, photos) ─────────────────────────

/**
 * A folder row from accounts_folders. `id` is null for the built-in "general"
 * folder, which cannot be renamed or deleted.
 *
 * The photo endpoints spell the name field `folder`; the getters below map it
 * to `name` so every folder-backed library hands the UI one shape.
 */
export interface LibraryFolder {
  id: number | null;
  name: string;
  count: number;
  /**
   * Photo folders only. 'master' folders come from the corporate dashboard and
   * are shared across the master account; 'account' folders are this account's
   * own. Template folders leave these undefined — they are already per-account.
   */
  scope?: 'master' | 'account';
  isSystem?: boolean;
  systemKey?: string;
}

// ─── Email Templates (Marketing) ─────────────────────────────────────────────

export interface EmailMarketingTemplate {
  emailTemplateId: number;
  name: string;
  subject: string | null;
  description: string | null;
  preheader: string | null;
  fromName: string | null;
  fromEmail: string | null;
  templateType: string;
  active: boolean;
  folder: string | null;
  createdAt: string;
  /**
   * Whether the DnD editor compiled this template. Computed server-side from the
   * marker on <body>, so a list can label a card without holding its markup —
   * which is what isDndHtml() needed the whole document for.
   */
  isDnd: boolean;
  /** Size of the compiled html. 0 means the template has no content yet. */
  htmlBytes: number;
  hasContent: boolean;
  /** Only present when the caller passed withContent: 1. */
  htmlContent?: string | null;
  /**
   * True when markup exists but was withheld to keep the response under the
   * gateway's size limit. Fetch it per template with getEmailTemplatePreview.
   */
  htmlOmitted?: boolean;
}

export interface CreateEmailTemplateData {
  name: string;
  subject?: string;
  description?: string;
  folder?: string;
}

/**
 * Template metadata. Markup is NOT included unless you pass withContent: 1, and
 * even then the API caps the total it will return — email bodies are large enough
 * that a couple of hundred of them exceed the response limit outright, which used
 * to fail the request and leave the caller with no templates at all.
 *
 * Lists should stay on the default and pull previews per visible card with
 * getEmailTemplatePreview; `isDnd`, `htmlBytes` and `hasContent` already cover
 * labelling and empty states.
 */
export function listEmailTemplates(params: ListParams): Promise<PaginatedResponse<EmailMarketingTemplate>> {
  return apiClient.get('/marketing/email-templates', { params }).then(unwrapList);
}

/**
 * Compiled html for a single template — the thumbnail source. Narrower than
 * getEmailTemplateFull, which also carries the DnD model, so this is the cheap
 * call for previews.
 */
export function getEmailTemplatePreview(id: number): Promise<string | null> {
  return apiClient
    .get(`/marketing/email-templates/${id}/preview`)
    .then((r) => (r.data?.data?.htmlContent ?? null) as string | null);
}

export function createEmailTemplate(data: CreateEmailTemplateData): Promise<EmailMarketingTemplate> {
  return apiClient.post('/marketing/email-templates', data).then(unwrap);
}

/**
 * Server-side clone: the API copies the stored row and its current version, so
 * subject, description, preheader, from name/email, template type and the whole
 * html/json/plain content all come across. Omit `name` to let the API pick the
 * first free "<name> (Copy N)" — it checks the account, not just the loaded page.
 *
 * `active` is the one field that never carries over: copies always land inactive.
 */
export function duplicateEmailTemplate(
  id: number,
  name?: string,
): Promise<{ emailTemplateId: number; name: string; active: false }> {
  return apiClient.post(`/marketing/email-templates/${id}/duplicate`, name ? { name } : {}).then(unwrap);
}

/** Soft delete — the API flips templateStatus to 0 and stamps removeDate. */
export function deleteEmailTemplate(id: number): Promise<void> {
  return apiClient.delete(`/marketing/email-templates/${id}`).then(() => undefined);
}

export interface TestEmailTemplateData {
  test_email: string;
  subject?: string;
  preheader?: string;
  from_email?: string;
  from_name?: string;
  html_content?: string;
  plain_content?: string;
}

/**
 * Send a [TEST]-prefixed copy of a template to one address, the same way a
 * campaign test send works. Pass the live editor content so an unsaved draft is
 * what gets tested — whatever is omitted falls back to the saved template and
 * its current version.
 */
export function sendTestEmailTemplate(id: number | string, data: TestEmailTemplateData): Promise<void> {
  return apiClient.post(`/marketing/email-templates/${id}/test`, data).then(() => undefined);
}

export function getEmailTemplateFolders(): Promise<LibraryFolder[]> {
  return apiClient.get('/marketing/email-templates/folders').then((r) => r.data?.data ?? []);
}

export function createEmailTemplateFolder(name: string): Promise<LibraryFolder> {
  return apiClient.post('/marketing/email-templates/folders', { name }).then(unwrap);
}

export function renameEmailTemplateFolder(id: number, name: string): Promise<LibraryFolder> {
  return apiClient.put(`/marketing/email-templates/folders/${id}`, { name }).then(unwrap);
}

export function deleteEmailTemplateFolder(id: number): Promise<void> {
  return apiClient.delete(`/marketing/email-templates/folders/${id}`).then(() => undefined);
}

// ─── Email Components (Marketing) ────────────────────────────────────────────
//
// A per-account library of reusable pre-built blocks. Each one shows up in the
// DnD editor's "Saved" palette next to the account default header/footer, and in
// the HTML editor's insert bar. `jsonContent` is the DnD model and the source of
// truth; `htmlContent` is the compiled form the HTML editor inserts — the two are
// always saved together.

export interface EmailComponent {
  emailComponentId: number;
  name: string;
  description: string | null;
  htmlContent: string | null;
  /** The DnD model, already JSON-decoded by the API. Only present with withContent. */
  jsonContent?: unknown;
  active: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface EmailComponentListParams {
  search?: string;
  /** 1 to include the (much larger) DnD model — the editor palette needs it. */
  withContent?: 0 | 1;
  /** 1 to hide components whose `active` flag is off. */
  activeOnly?: 0 | 1;
  page?: number;
  perPage?: number;
}

export interface SaveEmailComponentData {
  name?: string;
  description?: string;
  htmlContent?: string;
  jsonContent?: unknown;
  active?: boolean;
  sortOrder?: number;
}

export function listEmailComponents(
  params?: EmailComponentListParams,
): Promise<PaginatedResponse<EmailComponent>> {
  return apiClient.get('/marketing/email-components', { params }).then(unwrapList);
}

export function getEmailComponentFull(id: number): Promise<EmailComponent> {
  return apiClient.get(`/marketing/email-components/${id}/full`).then(unwrap);
}

export function createEmailComponent(
  data: SaveEmailComponentData,
): Promise<{ emailComponentId: number }> {
  return apiClient.post('/marketing/email-components', data).then(unwrap);
}

export function updateEmailComponent(
  id: number,
  data: SaveEmailComponentData,
): Promise<{ emailComponentId: number }> {
  return apiClient.put(`/marketing/email-components/${id}`, data).then(unwrap);
}

/** Soft delete — the API flips componentStatus to 0 and stamps removeDate. */
export function deleteEmailComponent(id: number): Promise<void> {
  return apiClient.delete(`/marketing/email-components/${id}`).then(() => undefined);
}

// ─── SMS Templates (Marketing) ───────────────────────────────────────────────

export interface SmsMarketingTemplate {
  smsTemplateId: number;
  name: string;
  description: string | null;
  messageBody: string | null;
  mediaUrls: string[] | null;
  folder: string | null;
  active: boolean;
  /** The API maps `createDate` to this on the way out, matching the email shape. */
  createdAt: string;
  updatedAt: string;
}

export interface CreateSmsTemplateData {
  name: string;
  messageBody?: string;
  folder?: string;
}

export function listSmsTemplates(params: ListParams): Promise<PaginatedResponse<SmsMarketingTemplate>> {
  return apiClient.get('/marketing/sms-templates', { params }).then(unwrapList);
}

export function createSmsTemplate(data: CreateSmsTemplateData): Promise<SmsMarketingTemplate> {
  return apiClient.post('/marketing/sms-templates', data).then(unwrap);
}

/**
 * Server-side clone — carries description and mediaUrls, not just the body.
 * Like the email one, the copy always comes back inactive.
 */
export function duplicateSmsTemplate(id: number, name?: string): Promise<SmsMarketingTemplate> {
  return apiClient.post(`/marketing/sms-templates/${id}/duplicate`, name ? { name } : {}).then(unwrap);
}

/** Soft delete — the API flips templateStatus to 0 and stamps removeDate. */
export function deleteSmsTemplate(id: number): Promise<void> {
  return apiClient.delete(`/marketing/sms-templates/${id}`).then(() => undefined);
}

/**
 * Send a test copy of a template to one phone number, the same way a campaign
 * test send works. Pass the live draft so unsaved edits are what gets tested;
 * omit it and the API uses the saved row.
 */
export function sendTestSmsTemplate(
  id: number,
  phone: string,
  draft?: { messageBody?: string; mediaUrls?: string[] },
): Promise<void> {
  return apiClient
    .post(`/marketing/sms-templates/${id}/test`, {
      phone,
      ...(draft?.messageBody !== undefined ? { messageBody: draft.messageBody } : {}),
      ...(draft?.mediaUrls !== undefined ? { mediaUrls: JSON.stringify(draft.mediaUrls) } : {}),
    })
    .then(() => undefined);
}

export function getSmsTemplateFolders(): Promise<LibraryFolder[]> {
  return apiClient.get('/marketing/sms-templates/folders').then((r) => r.data?.data ?? []);
}

export function createSmsTemplateFolder(name: string): Promise<LibraryFolder> {
  return apiClient.post('/marketing/sms-templates/folders', { name }).then(unwrap);
}

export function renameSmsTemplateFolder(id: number, name: string): Promise<LibraryFolder> {
  return apiClient.put(`/marketing/sms-templates/folders/${id}`, { name }).then(unwrap);
}

export function deleteSmsTemplateFolder(id: number): Promise<void> {
  return apiClient.delete(`/marketing/sms-templates/folders/${id}`).then(() => undefined);
}

// ─── Request-Info Links ──────────────────────────────────────────────────────
//
// A request-info link points at ONE resource (a product, package, appointment
// type or membership) plus an optional author note. It is resource-level: the
// same link can go in any number of campaigns, automation steps and templates,
// and the send pipeline appends a signed per-recipient suffix at send time, so
// every lead is credited to the send that delivered it.

export type RequestInfoResourceType = 'product' | 'package' | 'appointmentType' | 'membership';

/** The photo toggle, description toggle and author's description are RequestInfoDetailsDisplay. */
export interface RequestInfoLink extends RequestInfoDetailsDisplay {
  id: number;
  /** 7 lowercase base36 characters. */
  code: string;
  /** The bare link, `https://<host>/l/<code>` — what the Copy button puts on the clipboard. */
  url: string;
  /** The same link in the page's preview state (notice + inert button, no lead). Absent from older API builds. */
  previewUrl?: string;
  resourceType: RequestInfoResourceType;
  resourceId: number;
  /** Name stored when the link was generated, so a later rename or removal cannot blank it. */
  resourceName: string;
  /** '' when none. */
  note: string;
  /** The photo across the top of the page; null (or absent on older API builds) = the default photo. */
  heroImageUrl?: string | null;
  /** Which branding logo sits over the photo; absent on older API builds = auto. */
  logoVariant?: RequestInfoLogoVariant;
  /** The photo on the page shown after the tap; null (or absent) = the page's photo. */
  confirmHeroImageUrl?: string | null;
  /** The logo on the page shown after the tap; absent on older API builds = same as the page. */
  confirmLogoVariant?: RequestInfoConfirmLogoVariant;
  active: boolean;
  createdById: number;
  createdByName: string | null;
  /** ISO 8601. */
  createdAt: string | null;
  /** ISO 8601; null while active. */
  deactivatedAt: string | null;
  leadCount: number;
}

export interface RequestInfoLinksResponse {
  /** Newest first, deactivated links included. */
  links: RequestInfoLink[];
  /** The most characters personalization adds to one link in an SMS. */
  personalizedExtraChars: number;
  /** The account's logo and colours, for the generator's live preview. */
  branding?: RequestInfoBranding;
}

export interface CreateRequestInfoLinkData extends RequestInfoDetailsDisplay {
  resourceType: RequestInfoResourceType;
  resourceId: number;
  note: string;
  /** A photo-library URL for the top of the page; null = the default photo. */
  heroImageUrl?: string | null;
  logoVariant?: RequestInfoLogoVariant;
  /** The page shown after the tap; null / 'same' = as the page. */
  confirmHeroImageUrl?: string | null;
  confirmLogoVariant?: RequestInfoConfirmLogoVariant;
}

export function listRequestInfoLinks(): Promise<RequestInfoLinksResponse> {
  return apiClient.get('/marketing/request-info-links').then(unwrap);
}

/** What the landing page shows for a resource, so the generator can preview it before the link exists. */
export function getRequestInfoResource(
  resourceType: RequestInfoResourceType,
  resourceId: number,
): Promise<{ resource: RequestInfoResource }> {
  return apiClient
    .get('/marketing/request-info-links/resource', { params: { resourceType, resourceId } })
    .then(unwrap);
}

export function createRequestInfoLink(
  data: CreateRequestInfoLinkData,
): Promise<{ link: RequestInfoLink; personalizedExtraChars: number }> {
  return apiClient.post('/marketing/request-info-links', data).then(unwrap);
}

export interface UpdateRequestInfoLinkData extends RequestInfoDetailsDisplay {
  note?: string;
  /** null = back to the default photo. */
  heroImageUrl?: string | null;
  logoVariant?: RequestInfoLogoVariant;
  /** null / 'same' = back to following the page. */
  confirmHeroImageUrl?: string | null;
  confirmLogoVariant?: RequestInfoConfirmLogoVariant;
}

/**
 * Edit a link's note and the top image and logo of its page and after-tap page.
 * The resource is fixed: the link may already be in sent messages, so a
 * different resource needs a new link.
 */
export function updateRequestInfoLink(id: number, data: UpdateRequestInfoLinkData): Promise<{ link: RequestInfoLink }> {
  return apiClient.patch(`/marketing/request-info-links/${id}`, data).then(unwrap);
}

/** Deactivate (false) or reactivate (true). Links are never deleted. */
export function setRequestInfoLinkActive(id: number, active: boolean): Promise<{ link: RequestInfoLink }> {
  return apiClient.patch(`/marketing/request-info-links/${id}`, { active }).then(unwrap);
}

/**
 * Leads created through request-info links, split by who asked: the message's
 * own recipient, or someone the link was forwarded to ("Not Sarah?").
 */
export interface RequestInfoLeadCounts {
  recipient: number;
  referral: number;
}

/**
 * The `leads` counts off a stats payload (or one of its per-step rows), as
 * numbers. Missing reads as zeros: the API that adds them may deploy after
 * this UI, and a stats page must not break for want of a tile.
 */
export function leadCountsOf(
  source: { leads?: Partial<RequestInfoLeadCounts> | null } | null | undefined,
): RequestInfoLeadCounts {
  const toCount = (n: unknown) => {
    const v = Number(n);
    return Number.isFinite(v) && v > 0 ? v : 0;
  };
  return {
    recipient: toCount(source?.leads?.recipient),
    referral: toCount(source?.leads?.referral),
  };
}

// ─── Automations ─────────────────────────────────────────────────────────────

export interface Automation extends Partial<EmailAutomationSendHold> {
  automationId: number;
  name: string;
  description: string | null;
  channel: 'email' | 'sms';
  status: string;
  triggerType: string | null;
  stepCount: number;
  enrolledCount: number;
  createdAt: string;
}

export interface CreateAutomationData {
  name: string;
  channel: 'email' | 'sms';
  description?: string;
  triggerType?: string;
}

export function listAutomations(params: ListParams): Promise<PaginatedResponse<Automation>> {
  return apiClient.get('/marketing/automations', { params }).then(unwrapList);
}

export function createAutomation(data: CreateAutomationData): Promise<Automation> {
  return apiClient.post('/marketing/automations', data).then(unwrap);
}

export function activateAutomation(id: number): Promise<void> {
  return apiClient.post(`/marketing/automations/${id}/activate`);
}

export function pauseAutomation(id: number): Promise<void> {
  return apiClient.post(`/marketing/automations/${id}/pause`);
}

/**
 * The automation and its steps as metadata — step bodies are NOT included. Each
 * step stores compiled html, plain text and the DnD model, so returning them for
 * every step could exceed the response limit and fail the request outright.
 * Fetch the one step being opened with getAutomationStepContent.
 */
export function getAutomation(id: number): Promise<AutomationDetail> {
  return apiClient.get(`/marketing/automations/${id}`).then(unwrap);
}

export interface AutomationStepContent {
  stepId: number;
  htmlContent: string | null;
  plainContent: string | null;
  jsonContent: string | null;
}

/** The three content blobs for one step — bounded, so it cannot hit the limit. */
export function getAutomationStepContent(
  automationId: number,
  stepId: number,
): Promise<AutomationStepContent> {
  return apiClient
    .get(`/marketing/automations/${automationId}/steps/${stepId}/content`)
    .then(unwrap);
}

export function updateAutomation(id: number, data: Partial<CreateAutomationData> & {
  entryListId?: number | null;
  exitCriteria?: number;
  allowReentry?: boolean;
  unsubscribeGroup?: string;
  categories?: string;
}): Promise<AutomationDetail> {
  return apiClient.put(`/marketing/automations/${id}`, data).then(unwrap);
}

export function deleteAutomation(id: number): Promise<void> {
  return apiClient.delete(`/marketing/automations/${id}`);
}

export function stopAutomation(id: number): Promise<void> {
  return apiClient.post(`/marketing/automations/${id}/stop`);
}

export function syncAutomationContacts(id: number): Promise<{ added: number; removed: number; skipped: number }> {
  return apiClient.post(`/marketing/automations/${id}/sync-contacts`).then(unwrap);
}

/**
 * "Try again now" on a held email automation: ends the hour's wait on every
 * held step, so the next step run (every 15 minutes) tries one contact on each.
 * The hold stays until that try goes through.
 */
export function retryEmailAutomationSendHold(id: number): Promise<EmailAutomationSendHold> {
  return apiClient.post(`/marketing/automations/${id}/send-hold/retry`).then(unwrap);
}

export function getAutomationStats(id: number, params?: { dateFrom?: string; dateTo?: string }): Promise<AutomationStatsData> {
  const apiParams = params ? { date_from: params.dateFrom, date_to: params.dateTo } : undefined;
  return apiClient.get(`/marketing/automations/${id}/stats`, { params: apiParams }).then(unwrap);
}

// ─── Automation Contacts ─────────────────────────────────────────────────────

/**
 * Why an email automation contact was stopped as Invalid or Failed. '' for
 * every other status, and for contacts stopped before stop reasons were
 * recorded (the modal then explains the status instead).
 *   blank     — no email address on file
 *   malformed — the address on file is not a valid email address
 *   flagged   — the address is on the account's invalid-email list
 *   rejected  — SendGrid refused the address; it was added to that list
 *   cleared   — the linked patient's email was cleared
 *   retries   — three tries of the same step failed (status 'failed')
 */
export type EmailAutomationStopReason = 'blank' | 'malformed' | 'flagged' | 'rejected' | 'cleared' | 'retries' | '';

/** Email automation contact counts by status, as the contacts and stats endpoints return them. */
export interface EmailAutomationContactCounts {
  total: number;
  active: number;
  completed: number;
  removed: number;
  unsubscribed: number;
  /** Stopped before a send: no usable address, or SendGrid refused it. */
  invalid: number;
  /** Stopped after three failed tries of the same step. */
  failed: number;
}

/**
 * One held step of an email automation. Dates are Pacific wall-clock.
 */
export interface EmailAutomationStepHold {
  stepId: number;
  stepOrder: number;
  /** SendGrid's message. */
  reason: string;
  since: string | null;
  lastTry: string | null;
  /** null after "Try again now": the next step run, within 15 minutes. */
  nextTry: string | null;
}

/**
 * An email automation's held steps. A step holds after SendGrid refuses it for
 * a reason that is not the recipient's (an unverified sender, an outage): it
 * tries one contact an hour while the other steps keep sending, and held tries
 * do not count against contacts.
 */
export interface EmailAutomationSendHold {
  /** The first held step's message; '' when no step is held. */
  sendHoldReason: string;
  sendHoldSteps: EmailAutomationStepHold[];
}

export interface AutomationContact {
  contactId: number;
  email: string;
  patientId: number | null;
  firstName: string;
  lastName: string;
  status: string;
  stopReason: EmailAutomationStopReason;
  currentStepOrder: number;
  totalSteps: number;
  opens: number;
  clicks: number;
  addedDate: string | null;
  removedDate: string | null;
  completedDate: string | null;
  /** Request-info leads this contact's emails produced. Absent on older responses. */
  leads?: ContactLead[];
  /** This contact's tries on the step the list is filtered to; null without a step filter. */
  stepSend?: EmailAutomationStepSend | null;
}

/**
 * One contact's tries on one step. The try shown (status, reason, time) is the
 * last one MATCHING the outcome filter, so a contact listed under Failed whose
 * retry went through still shows the failure; `latestDeliveryStatus` is how
 * the step ended.
 */
export interface EmailAutomationStepSend {
  attempts: number;
  deliveryStatus: string | null;
  /** SendGrid's sentence for a failed, held or bounced try. */
  reason: string | null;
  attemptedAt: string | null;
  opened: boolean;
  clicked: boolean;
  latestDeliveryStatus: string | null;
}

/**
 * The email Outcome filter. Each counts the way the stats page's boxes do:
 * Sent leaves out tries that never went out (failed, held, unconfirmed).
 * Replied is a reply to the automation (to the step, with one picked).
 */
export type EmailStepOutcome = 'sent' | 'delivered' | 'opened' | 'clicked' | 'bounced' | 'failed' | 'unsubscribed' | 'spam' | 'replied';

export interface AutomationContactsResponse {
  data: AutomationContact[];
  meta: { total: number; page: number; perPage: number; totalPages: number };
  contactCounts: EmailAutomationContactCounts;
  /** Contacts whose emails produced a request-info lead: the `lead` filter's counts (one step's, with a step picked). */
  leadCount?: number;
  leadRecipientCount?: number;
  leadReferralCount?: number;
  totalSteps: number;
  /** The step and outcome filters that applied (anything unrecognised is dropped). */
  stepId?: number | null;
  outcome?: EmailStepOutcome | null;
}

/**
 * `stepId` is the step's id, never its order (a send's stepOrder is frozen at
 * send time). `stepId` and `outcome` both mean "has at least one such send".
 */
export function listAutomationContacts(id: number, params?: { search?: string; status?: string; lead?: ContactLeadParam; stepId?: number; outcome?: EmailStepOutcome; page?: number; perPage?: number }): Promise<AutomationContactsResponse> {
  return apiClient.get(`/marketing/automations/${id}/contacts`, { params }).then(r => r.data);
}

/**
 * One send attempt on an email automation contact: an accepted send, a failed
 * try, or a `held` try (an account-wide SendGrid problem that did not count
 * against the contact). Ordered by step, then attempt time, then id.
 */
export interface AutomationContactSend {
  sendId: number;
  stepId: number | null;
  stepOrder: number;
  /** sent, processed, delivered, deferred, bounce, dropped, failed, held — older rows may hold others. */
  deliveryStatus: string | null;
  /** SendGrid's rejection text, or the bounce/drop reason the webhook wrote. */
  reason: string | null;
  open: number;
  clicks: number;
  spamReport: number;
  unsubscribe: number;
  /** Null for a held try: nothing went out. */
  sentAt: string | null;
  /** When the try was made (sentAt, or the row's create date). Always set. */
  attemptedAt: string | null;
  subject: string | null;
  senderEmail: string | null;
}

export interface AutomationContactStatsData {
  contact: {
    contactId: number;
    email: string;
    firstName: string;
    lastName: string;
    patientId: number | null;
    status: string;
    stopReason: EmailAutomationStopReason;
    currentStepOrder: number;
    nextSendAt: string | null;
    addedDate: string | null;
    removedDate: string | null;
    completedDate: string | null;
    /** Last write to the contact — when a stop happened. */
    updatedAt: string | null;
  };
  steps: AutomationContactSend[];
  /** Request-info leads, each on the step (stepId) it came from. Absent on older responses. */
  leads?: ContactLead[];
}

export function getAutomationContactStats(automationId: number, contactId: number): Promise<AutomationContactStatsData> {
  return apiClient.get(`/marketing/automations/${automationId}/contacts/${contactId}/stats`).then(unwrap);
}

// ─── Automation Steps ────────────────────────────────────────────────────────

export interface AutomationStep {
  stepId: number;
  stepOrder: number;
  delayMinutes: number;
  subject?: string;
  preheader?: string;
  senderEmail?: string;
  senderName?: string;
  /**
   * Present only once this step's body has been fetched (getAutomationStepContent)
   * or written locally. Steps arrive from the API without their bodies — an
   * automation's steps together were large enough to exceed the response limit,
   * which failed the whole request and left the page blank.
   */
  htmlContent?: string;
  plainContent?: string;
  jsonContent?: string | null;
  /** Whether html is stored for this step. Use instead of testing htmlContent. */
  hasContent?: boolean;
  /** Hint only — the editor routes on the real jsonContent it fetches. */
  isDnd?: boolean;
  htmlBytes?: number;
  jsonBytes?: number;
  messageBody?: string;
  mediaUrls?: string[];
  sent?: number;
  delivered?: number;
  opens?: number;
  clicks?: number;
  bounces?: number;
}

export interface AutomationDetail extends Automation {
  entryListId: number | null;
  entryListName: string | null;
  exitCriteria: number;
  allowReentry: boolean;
  unsubscribeGroup: string | null;
  categories: string | null;
  steps: AutomationStep[];
  contactCounts: {
    active: number;
    completed: number;
    removed: number;
    unsubscribed: number;
    total: number;
  };
}

export type AutomationStepStats = AutomationStep & {
  /** Every try on the step, failed, held and unconfirmed included. Absent from older API builds. */
  attempts?: number;
  sent: number;
  failedAttempts: number;
  delivered: number;
  opens: number;
  clicks: number;
  bounces: number;
  /** Distinct repliers to this step. Absent from older API builds. */
  replies?: number;
  /** Request-info leads credited to this step. Absent from older API builds. */
  leads?: RequestInfoLeadCounts;
};

export interface AutomationStatsData {
  sends: {
    /** Sends SendGrid accepted — failed, held and unconfirmed tries are not counted. */
    totalSent: number;
    /** Failed tries, counted on their own (a retried step adds one per try). */
    failedAttempts: number;
    delivered: number;
    opens: number;
    clicks: number;
    bounces: number;
    unsubscribes: number;
    spamReports: number;
    /** Distinct repliers. Absent from older API builds. */
    replies?: number;
  };
  contactCounts: EmailAutomationContactCounts;
  dailyStats?: Array<{ date: string; opens: number; clicks: number }>;
  steps?: AutomationStepStats[];
  /** Steps taken out of the automation that still have sends. Absent from older API builds. */
  removedSteps?: Array<AutomationStepStats & { previousStepOrder: number }>;
  clickTracking?: Array<{ url: string; clicks: number }>;
  /** Request-info leads across the automation. Absent from older API builds — read via leadCountsOf(). */
  leads?: RequestInfoLeadCounts;
}

export interface AutomationListStats {
  total: number;
  active: number;
  draft: number;
  paused: number;
  stopped: number;
  totalEnrollments: number;
  activeEnrollments: number;
}

export function getAutomationListStats(): Promise<AutomationListStats> {
  return apiClient.get('/marketing/automations/stats').then(unwrap);
}

export interface SmsCampaignListStats {
  total: number;
  draft: number;
  scheduled: number;
  sent: number;
}

export function getSmsCampaignListStats(): Promise<SmsCampaignListStats> {
  return apiClient.get('/marketing/sms-campaigns/stats').then(unwrap);
}

export interface PushCampaignListStats {
  total: number;
  draft: number;
  scheduled: number;
  sent: number;
}

export function getPushCampaignListStats(): Promise<PushCampaignListStats> {
  return apiClient.get('/marketing/push-campaigns/stats').then(unwrap);
}

export function listAutomationSteps(automationId: number): Promise<AutomationStep[]> {
  return apiClient.get(`/marketing/automations/${automationId}/steps`).then((r) => r.data.data);
}

export function addAutomationStep(automationId: number, data: Partial<AutomationStep>): Promise<AutomationStep> {
  return apiClient.post(`/marketing/automations/${automationId}/steps`, data).then(unwrap);
}

export function updateAutomationStep(automationId: number, stepId: number, data: Partial<AutomationStep>): Promise<AutomationStep> {
  return apiClient.put(`/marketing/automations/${automationId}/steps/${stepId}`, data).then(unwrap);
}

export function deleteAutomationStep(automationId: number, stepId: number): Promise<void> {
  return apiClient.delete(`/marketing/automations/${automationId}/steps/${stepId}`);
}

/**
 * Rewrite the whole step sequence. One bulk call rather than a PUT per moved
 * step, so the cron never observes two steps sharing a position mid-reorder.
 * `stepIds` must be every active step of the automation, in the new order.
 *
 * The server also re-anchors contacts that are mid-sequence to the step they
 * last actually received; `contactsRemapped` reports how many it moved.
 */
export function reorderAutomationSteps(
  automationId: number,
  stepIds: number[],
): Promise<{ steps: AutomationStep[]; contactsRemapped: number }> {
  return apiClient
    .put(`/marketing/automations/${automationId}/steps/reorder`, { stepIds })
    .then((r) => ({
      steps: r.data.data as AutomationStep[],
      contactsRemapped: (r.data.meta?.contactsRemapped ?? 0) as number,
    }));
}

// ─── SMS Automation Endpoints ───────────────────────────────────────────────

export function getSmsAutomation(id: number): Promise<AutomationDetail> {
  return apiClient.get(`/marketing/automations/sms/${id}`).then(unwrap);
}

export function updateSmsAutomation(id: number, data: Partial<CreateAutomationData> & {
  entryListId?: number | null;
  exitCriteria?: number;
  allowReentry?: boolean;
}): Promise<AutomationDetail> {
  return apiClient.put(`/marketing/automations/sms/${id}`, data).then(unwrap);
}

export function deleteSmsAutomation(id: number): Promise<void> {
  return apiClient.delete(`/marketing/automations/sms/${id}`);
}

export function activateSmsAutomation(id: number): Promise<void> {
  return apiClient.post(`/marketing/automations/sms/${id}/activate`);
}

export function pauseSmsAutomation(id: number): Promise<void> {
  return apiClient.post(`/marketing/automations/sms/${id}/pause`);
}

export function addSmsAutomationStep(automationId: number, data: Partial<AutomationStep>): Promise<AutomationStep> {
  return apiClient.post(`/marketing/automations/sms/${automationId}/steps`, data).then(unwrap);
}

export function updateSmsAutomationStep(automationId: number, stepId: number, data: Partial<AutomationStep>): Promise<AutomationStep> {
  return apiClient.put(`/marketing/automations/sms/${automationId}/steps/${stepId}`, data).then(unwrap);
}

export function deleteSmsAutomationStep(automationId: number, stepId: number): Promise<void> {
  return apiClient.delete(`/marketing/automations/sms/${automationId}/steps/${stepId}`);
}

/** SMS counterpart of reorderAutomationSteps(). */
export function reorderSmsAutomationSteps(
  automationId: number,
  stepIds: number[],
): Promise<{ steps: AutomationStep[]; contactsRemapped: number }> {
  return apiClient
    .put(`/marketing/automations/sms/${automationId}/steps/reorder`, { stepIds })
    .then((r) => ({
      steps: r.data.data as AutomationStep[],
      contactsRemapped: (r.data.meta?.contactsRemapped ?? 0) as number,
    }));
}

export function getSmsAutomationStats(id: number, params?: { date_from?: string; date_to?: string }): Promise<any> {
  return apiClient.get(`/marketing/automations/sms/${id}/stats`, { params }).then(unwrap);
}

export interface SmsAutomationContactRow {
  contactId: number;
  phoneNumber: string;
  firstName: string | null;
  lastName: string | null;
  patientID: number | null;
  status: string;
  currentStepOrder: number;
  nextSendAt: string | null;
  addedDate: string;
  completedDate: string | null;
  /** Live sms_unsubscribes state — historical opt-outs can still have status 'active'. */
  optedOut: boolean;
  /**
   * This contact's outcome on the step the list is filtered to. Null unless a
   * stepId filter is active. `attempts` counts every send row for that step, so
   * it exceeds 1 wherever a transient failure was retried.
   */
  stepSend?: SmsAutomationStepSend | null;
  /** Request-info leads this contact's texts produced. Absent on older responses. */
  leads?: ContactLead[];
}

export interface SmsAutomationStepSend {
  attempts: number;
  /**
   * The last attempt MATCHING the outcome filter, not simply the last attempt —
   * a contact listed under "failed" whose retry later succeeded must still show
   * the failure that put it there.
   */
  deliveryStatus: string | null;
  errorMessage: string | null;
  /** COALESCE(sentAt, createDate) — a failed send never sets sentAt. */
  attemptedAt: string | null;
  statusUpdatedAt: string | null;
  /** How the step ended up, which differs from deliveryStatus after a retry. */
  latestDeliveryStatus: string | null;
}

export interface SmsAutomationSend {
  sendId: number;
  /** Absent on older responses. */
  stepId?: number | null;
  stepOrder: number;
  deliveryStatus: string;
  twilioMessageSid: string | null;
  errorMessage: string | null;
  sentAt: string | null;
  attemptedAt: string;
  /** For delivered/failed rows this is when the status callback landed. */
  statusUpdatedAt: string | null;
  messageBody: string | null;
}

export interface SmsAutomationContactEvent {
  eventType: string;
  eventDate: string;
  errorCode: string | null;
  errorMessage: string | null;
  twilioMessageSid: string | null;
}

export interface SmsAutomationUpcomingStep {
  stepId: number;
  stepOrder: number;
  /** Wait before this step, measured from the previous send. */
  delayMinutes: number;
  messagePreview: string;
  /** Projected send time (Pacific wall clock, like every other DATETIME here). */
  scheduledAt: string;
  /** The step the contact is currently waiting on. */
  isNext: boolean;
}

export interface SmsAutomationContactDetail {
  contact: {
    contactId: number;
    phoneNumber: string;
    firstName: string | null;
    lastName: string | null;
    patientID: number | null;
    status: string;
    currentStepOrder: number;
    nextSendAt: string | null;
    addedDate: string;
    removedDate: string | null;
    completedDate: string | null;
    /** Fallback "stopped at" time: opt-out/invalid writers set status only. */
    updatedAt?: string | null;
    optedOut: boolean;
  };
  sends: SmsAutomationSend[];
  events: SmsAutomationContactEvent[];
  /** Empty unless the contact is active with a scheduled next send. */
  upcomingSteps: SmsAutomationUpcomingStep[];
  /** Projections assume the automation keeps running; paused ones send nothing. */
  automationStatus: string;
  /** Request-info leads, each on the step (stepId) it came from. Absent on older responses. */
  leads?: ContactLead[];
}

/**
 * `stepId` is the step's id, never its order: a send row's stepOrder is frozen at
 * send time and goes stale as soon as steps are added, reordered or deleted.
 *
 * `sendStatus` narrows to a delivery outcome and works with or without a step. It
 * takes a comma-separated list, because a step card's "Sent" counts `sent` AND
 * `delivered` — a drill-down from it has to ask for `sent,delivered` or it returns
 * only the ones still awaiting a receipt.
 *
 * `date_from`/`date_to` scope the SENDS the filters look at, matching the range the
 * stats page computed the clicked number over. They do not filter the contacts
 * themselves, which are current state and have no such date.
 *
 * `lead` keeps the contacts whose texts produced a request-info lead
 * (`leadCount` of them; one kind with 'recipient' / 'referral'), alongside any
 * other filter. With `stepId`, only leads from that step count.
 */
export function listSmsAutomationContacts(id: number, params: { page?: number; perPage?: number; search?: string; status?: string; lead?: ContactLeadParam; stepId?: number; sendStatus?: string; date_from?: string; date_to?: string }): Promise<{ contacts: SmsAutomationContactRow[]; total: number; leadCount?: number; leadRecipientCount?: number; leadReferralCount?: number; page: number; perPage: number; stepId: number | null; sendStatus: string | null; dateFrom: string | null; dateTo: string | null }> {
  return apiClient.get(`/marketing/automations/sms/${id}/contacts`, { params }).then(unwrap);
}

export function getSmsAutomationContactStats(automationId: number, contactId: number): Promise<SmsAutomationContactDetail> {
  return apiClient.get(`/marketing/automations/sms/${automationId}/contacts/${contactId}/stats`).then(unwrap);
}

export function syncSmsAutomationContacts(id: number): Promise<{ added: number; removed?: number; skipped: number }> {
  return apiClient.post(`/marketing/automations/sms/${id}/sync-contacts`).then(unwrap);
}

export function stopSmsAutomation(id: number): Promise<void> {
  return apiClient.post(`/marketing/automations/sms/${id}/stop`);
}

export function listEntryLists(channel: 'email' | 'sms'): Promise<Array<{ emailListId: number; name: string; listType: string; patientCount: number; uniqueEmailCount?: number; uniquePhoneCount?: number }>> {
  return apiClient.get('/marketing/lists', { params: { channel, perPage: 500, active: 1 } }).then((r) => r.data.data);
}

// ─── Audience Lists ──────────────────────────────────────────────────────────

export interface AudienceList {
  emailListId: number;
  name: string;
  description: string | null;
  listType: 'static' | 'dynamic';
  patientCount: number;
  uniqueEmailCount?: number;
  uniquePhoneCount?: number;
  active: boolean;
  createdAt: string;
}

export interface CreateListData {
  name: string;
  description?: string;
  listType: 'static' | 'dynamic';
  /** Lists are created inactive (a draft) unless this is set. */
  active?: boolean;
  /**
   * Static lists only: the audience to snapshot. The API resolves it the same
   * way resolveRecipients does and writes the members on create. A dynamic
   * list takes its filter through updateAudienceList instead.
   */
  sourceType?: string;
  filterConditions?: unknown;
}

export function listAudienceLists(params: ListParams): Promise<PaginatedResponse<AudienceList>> {
  return apiClient.get('/marketing/lists', { params }).then(unwrapList);
}

export function createAudienceList(data: CreateListData): Promise<AudienceList> {
  return apiClient.post('/marketing/lists', data).then(unwrap);
}

export function deleteAudienceList(id: number): Promise<void> {
  return apiClient.delete(`/marketing/lists/${id}`);
}

// ─── Audience Segments ───────────────────────────────────────────────────────

export interface AudienceSegment {
  emailSegmentId: number;
  name: string;
  description: string | null;
  sourceType: string | null;
  filterConditions: unknown;
  estimatedCount: number;
  active: boolean;
  createdAt: string;
}

export interface CreateSegmentData {
  name: string;
  description?: string;
  sourceType?: string;
  filterConditions?: unknown;
}

export function listSegments(params: ListParams): Promise<PaginatedResponse<AudienceSegment>> {
  return apiClient.get('/marketing/segments', { params }).then(unwrapList);
}

export function createSegment(data: CreateSegmentData): Promise<AudienceSegment> {
  return apiClient.post('/marketing/segments', data).then(unwrap);
}

export function getSegment(id: number): Promise<AudienceSegment> {
  return apiClient.get(`/marketing/segments/${id}`).then(unwrap);
}

export function updateSegment(id: number, data: Partial<CreateSegmentData>): Promise<AudienceSegment> {
  return apiClient.put(`/marketing/segments/${id}`, data).then(unwrap);
}

export function deleteSegment(id: number): Promise<void> {
  return apiClient.delete(`/marketing/segments/${id}`);
}

export interface SegmentPreviewResult {
  total: number;
  uniqueEmailCount?: number;
  uniquePhoneCount?: number;
  recipients: Array<{ email: string; firstName: string | null; lastName: string | null; phone: string | null }>;
}

export function previewSegment(id: number, params?: { page?: number; perPage?: number; search?: string }): Promise<SegmentPreviewResult> {
  return apiClient.get(`/marketing/segments/${id}/preview`, { params }).then(unwrap);
}

export function previewSegmentFilters(data: { sourceType: string; filterConditions: unknown; page?: number; perPage?: number; search?: string }): Promise<SegmentPreviewResult> {
  return apiClient.post('/marketing/segments/preview', data).then(unwrap);
}

export function getSegmentFilters(sourceType: string): Promise<FilterGroup[]> {
  return apiClient.get('/marketing/segments/filters', { params: { sourceType } }).then(r => r.data.data);
}

export interface ResolveRecipientsResult {
  total: number;
  uniqueEmailCount: number;
  uniquePhoneCount: number;
  recipients: Array<{ email: string; firstName: string | null; lastName: string | null; phone: string | null; patientId?: number }>;
}

export function resolveRecipients(data: {
  sourceType: string;
  filterConditions: unknown;
  filterPlus?: string[];
  page?: number;
  perPage?: number;
  search?: string;
}): Promise<ResolveRecipientsResult> {
  return apiClient.post('/marketing/recipients/resolve', data).then(unwrap);
}

export interface FilterGroup {
  key: string;
  label: string;
  type: 'checkbox' | 'daterange' | 'range';
  options?: Array<{ value: string; label: string }>;
  /** For type 'range' — the slider/input bounds (e.g. age 0–120). */
  min?: number;
  max?: number;
}

// ─── Audience List Detail ───────────────────────────────────────────────────

export interface AudienceListDetail extends AudienceList {
  /**
   * The list's data source, derived server-side from the stored
   * `filterConditions.source_type` (MarketingController, the /marketing/lists
   * read paths) and null for a list that has none yet. `updateAudienceList`
   * already accepted it on the way IN; only the read type was missing it, so
   * ContactListDetailPage's "Data source is locked after save" check and the
   * disabled state on the picker were reading an undeclared field.
   *
   * Deliberately `string | null` and not a union of SOURCE_TYPE_OPTIONS: the
   * backend reads whatever is stored with `?? null` and does not validate it
   * back into a vocabulary on the way out.
   */
  sourceType: string | null;
  uniqueEmailCount: number;
  uniquePhoneCount: number;
  updatedAt: string | null;
  segmentIds: number[];
  linkedAutomations: Array<{ id: number; name: string; status: string; channel: 'email' | 'sms' }>;
}

export function getAudienceList(id: number): Promise<AudienceListDetail> {
  return apiClient.get(`/marketing/lists/${id}`).then(unwrap);
}

/**
 * What `PUT /marketing/lists/{id}` actually returns — `{ id, updated }`, plus
 * the three counts ONLY when the request carried `filterConditions` and the
 * server re-ran the segment query (MarketingController::updateList). It is not
 * an AudienceListDetail; declaring it as one is what pushed ContactListDetailPage
 * into annotating its own `onSuccess` as `Record<string, unknown>`.
 */
export interface UpdateAudienceListResult {
  id: number;
  updated: boolean;
  patientCount?: number;
  uniqueEmailCount?: number;
  uniquePhoneCount?: number;
}

export function updateAudienceList(id: number, data: Partial<CreateListData & { active?: boolean; segmentIds?: number[]; sourceType?: string; filterConditions?: unknown }>): Promise<UpdateAudienceListResult> {
  return apiClient.put(`/marketing/lists/${id}`, data).then(unwrap);
}

export interface ListMember {
  id: number;
  patientId: number;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  countryCode: string | null;
  active: boolean;
  addedAt: string | null;
}

export function getListMembers(listId: number, params?: { page?: number; perPage?: number; search?: string; showRemoved?: boolean }): Promise<PaginatedResponse<ListMember>> {
  return apiClient.get(`/marketing/lists/${listId}/members`, { params }).then(unwrapList);
}

export function addListMember(listId: number, patientId: number): Promise<void> {
  return apiClient.post(`/marketing/lists/${listId}/members`, { patientId });
}

export function removeListMember(listId: number, memberId: number): Promise<void> {
  return apiClient.delete(`/marketing/lists/${listId}/members/${memberId}`);
}

export function restoreListMember(listId: number, memberId: number): Promise<void> {
  return apiClient.post(`/marketing/lists/${listId}/members/${memberId}/restore`);
}

// ─── Push Notification Campaigns ────────────────────────────────────────────

export interface PushCampaign {
  pushCampaignId: number;
  name: string;
  description: string | null;
  title: string | null;
  messageBody: string | null;
  imageUrl: string | null;
  deepLink: string | null;
  status: string;
  scheduledAt: string | null;
  sentAt: string | null;
  totalRecipients: number;
  /**
   * Whether account (staff) users are excluded from the audience.
   * MarketingController returns it as `(bool) ($row->accountUsers ?? false)`
   * and accepts it on update; the type never declared it, so
   * PushCampaignEditorPage's `!!campaign.accountUsers` read an undeclared
   * field and the toggle never reflected a saved campaign.
   */
  accountUsers: boolean;
  createDate: string;
}

export interface CreatePushCampaignData {
  name: string;
  description?: string;
  title?: string;
  messageBody?: string;
}

export type PushRecipientStatus =
  | 'queued'
  | 'sending'
  | 'sent'
  | 'delivered'
  | 'failed'
  | 'opened'
  | 'clicked'
  | 'no_device'
  | 'unsubscribed';

/**
 * Filter values accepted by getPushCampaignContacts.
 * - 'not_received' bundles failed + no_device (= didn't get the push)
 * - 'not_opened'   bundles sent + delivered without an open
 * - 'engaged'      bundles opened + clicked
 */
export type PushContactFilter = PushRecipientStatus | 'not_received' | 'not_opened' | 'engaged';

export interface PushCampaignContact {
  pushCampaignRecipientId: number;
  patientID: number;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phoneNumber: string | null;
  hasPushToken: boolean;
  deliveryStatus: PushRecipientStatus;
  sentVia: 'apns' | 'fcm' | null;
  sentAt: string | null;
  deliveredAt: string | null;
  openedAt: string | null;
  clickedAt: string | null;
  errorCode: string | null;
  errorMessage: string | null;
}

export interface PushCampaignContactsParams {
  page?: number;
  perPage?: number;
  status?: PushContactFilter;
  search?: string;
}

export interface PushCampaignList {
  emailListId: number;
  listName: string;
  listType: 'static' | 'dynamic';
  memberCount: number;
  pushTokenCount: number;
}

export interface PushRecipientCount {
  count: number;
  withToken: number;
}

export function listPushCampaigns(params: ListParams): Promise<PaginatedResponse<PushCampaign>> {
  return apiClient.get('/marketing/push-campaigns', { params }).then(unwrapList);
}

export function createPushCampaign(data: CreatePushCampaignData): Promise<PushCampaign> {
  return apiClient.post('/marketing/push-campaigns', data).then(unwrap);
}

export function getPushCampaign(id: number): Promise<PushCampaign> {
  return apiClient.get(`/marketing/push-campaigns/${id}`).then(unwrap);
}

export function updatePushCampaign(id: number, data: Partial<CreatePushCampaignData & { imageUrl?: string; deepLink?: string; scheduledAt?: string; accountUsers?: boolean }>): Promise<PushCampaign> {
  return apiClient.put(`/marketing/push-campaigns/${id}`, data).then(unwrap);
}

export function deletePushCampaign(id: number): Promise<void> {
  return apiClient.delete(`/marketing/push-campaigns/${id}`);
}

export function getPushCampaignContacts(
  id: number,
  params: PushCampaignContactsParams = {},
): Promise<PaginatedResponse<PushCampaignContact>> {
  return apiClient
    .get(`/marketing/push-campaigns/${id}/contacts`, { params })
    .then(unwrapList);
}

export function getPushCampaignLists(id: number): Promise<PushCampaignList[]> {
  return apiClient.get(`/marketing/push-campaigns/${id}/lists`).then(r => r.data.data);
}

export function addPushCampaignList(id: number, listId: number): Promise<{ totalRecipients: number }> {
  return apiClient.post(`/marketing/push-campaigns/${id}/lists`, { listId }).then(unwrap);
}

export function removePushCampaignList(id: number, listId: number): Promise<{ totalRecipients: number }> {
  return apiClient.delete(`/marketing/push-campaigns/${id}/lists/${listId}`).then(r => r.data.data ?? { totalRecipients: 0 });
}

export function getPushCampaignRecipientCount(id: number): Promise<PushRecipientCount> {
  return apiClient.get(`/marketing/push-campaigns/${id}/recipient-count`).then(unwrap);
}

export interface PushSendResult {
  queued: number;
  noDevice: number;
  totalCandidates: number;
}

export function sendPushCampaign(id: number): Promise<PushSendResult> {
  return apiClient.post(`/marketing/push-campaigns/${id}/send`).then(unwrap);
}

export function schedulePushCampaign(id: number, scheduledAt: string): Promise<void> {
  return apiClient.post(`/marketing/push-campaigns/${id}/schedule`, { scheduledAt });
}

export function cancelPushSchedule(id: number): Promise<void> {
  return apiClient.post(`/marketing/push-campaigns/${id}/cancel-schedule`);
}

export interface PushCampaignStats extends CampaignStats {
  totalRecipients: number;
  totalQueued: number;
  totalSending: number;
  totalNoDevice: number;
}

export function getPushCampaignStats(id: number): Promise<PushCampaignStats> {
  return apiClient.get(`/marketing/push-campaigns/${id}/stats`).then(unwrap);
}

export function sendTestPush(id: number, patientId: number): Promise<void> {
  return apiClient.post(`/marketing/push-campaigns/${id}/test`, { patientId });
}

export interface PushResendResult {
  requeued: number;
  stillNoDevice: number;
}

export function resendFailedPushCampaign(
  id: number,
  recipientIds?: number[],
): Promise<PushResendResult> {
  const body = recipientIds && recipientIds.length > 0 ? { recipientIds } : {};
  return apiClient.post(`/marketing/push-campaigns/${id}/resend-failed`, body).then(unwrap);
}

// ─── History (who did what, when) ────────────────────────────────────────────

export type MarketingHistoryType =
  | 'email_campaign'
  | 'sms_campaign'
  | 'push_campaign'
  | 'email_automation'
  | 'sms_automation';

/** One changed field. Content-style fields (an email body, MMS media) carry no values. */
export interface MarketingHistoryChange {
  field: string;
  from?: string;
  to?: string;
}

export interface MarketingHistoryEvent {
  /** 0 for the synthetic "create" row built from a campaign's own created_by. */
  id: number;
  action: string;
  fromStatus: string;
  toStatus: string;
  stepId: number;
  details: {
    changes?: MarketingHistoryChange[];
    stepOrder?: number;
    subject?: string;
    message?: string;
    order?: number[];
    scheduledAt?: string;
    via?: 'manual' | 'schedule';
    recipients?: number;
    noDevice?: number;
    error?: string;
    queued?: number;
    stuckReset?: number;
    count?: number;
    requeued?: number;
    stillNoDevice?: number;
    clonedFromId?: number;
    clonedFromName?: string;
  } | null;
  userId: number;
  userName: string;
  /** Pacific wall-clock 'YYYY-MM-DD HH:MM:SS' (config('app.timezone')). */
  createdAt: string;
}

export function getMarketingHistory(type: MarketingHistoryType, id: number): Promise<MarketingHistoryEvent[]> {
  return apiClient.get(`/marketing/history/${type}/${id}`).then(unwrap);
}
